// @ts-check
// Identidade visual da escola em Configurações > Geral (contrato 08 de configurações, seção 3.3): as três cores da
// paleta, o logo completo e a prévia do e-mail que a família recebe. A prévia usa o mesmo layout do Worker
// (shared/branding/institution-email-layout.js), num iframe com sandbox vazio, como todo HTML de e-mail na tela.
import { html } from 'htm/preact';
import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { SettingsSection } from './settings-pages.js';
import { replaceCurrentTenantLocally, sessionState } from '../../data/metadata-store.js';
import { updateWorkspace, uploadImage } from '../../data/workspace-settings.js';
import { BRAND_COLOR_KEYS, DEFAULT_BRAND_COLORS, contrastRatio, normalizeBrandColor, readBrandColors } from '../../../../shared/branding/institution-brand.js';
import { renderInstitutionEmail } from '../../../../shared/branding/institution-email-layout.js';
import { Button } from '../../ui/button.js';
import { showErrorSnackbar, showSuccessSnackbar } from '../../ui/snackbar.js';
import { t } from '../../i18n/index.js';

// o logo completo vai nos e-mails: só formatos que Gmail e Outlook mostram
const BRAND_LOGO_TYPES = ['image/png', 'image/jpeg', 'image/gif'];
const MAXIMUM_BRAND_LOGO_BYTES = 2 * 1024 * 1024;

/** @param {string | null | undefined} path */
const absoluteUrl = (path) => (path ? new URL(path, window.location.origin).toString() : null);

/**
 * Endereço que o iframe isolado da prévia consegue abrir: um blob: (imagem enviada no modo demonstração) é de outra
 * origem para o iframe com sandbox vazio, então vira data: aqui; endereço https fica como está.
 * @param {string | null | undefined} path
 */
function usePreviewImageUrl(path) {
  const absolute = absoluteUrl(path);
  const [url, setUrl] = useState(absolute?.startsWith('blob:') ? null : absolute);
  useEffect(() => {
    if (!absolute?.startsWith('blob:')) {
      setUrl(absolute);
      return undefined;
    }
    let isCurrent = true;
    fetch(absolute)
      .then((response) => response.blob())
      .then((blob) => new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result));
        reader.readAsDataURL(blob);
      }))
      .then((dataUrl) => {
        if (isCurrent) setUrl(/** @type {string} */ (dataUrl));
      })
      .catch(() => {});
    return () => {
      isCurrent = false;
    };
  }, [absolute]);
  return url;
}

/**
 * E-mail de exemplo (convite de visita) com a identidade que está na tela, salva ou não.
 * @param {{ name: string, colors: import('../../../../shared/branding/institution-brand.js').BrandColors, logoUrl: string | null, iconUrl: string | null }} brand
 */
export function brandPreviewHtml(brand) {
  return renderInstitutionEmail({
    brand,
    preheader: t('workspaceSettings.brand.preview.preheader'),
    badge: t('workspaceSettings.brand.preview.badge'),
    title: t('workspaceSettings.brand.preview.title'),
    paragraphs: [t('workspaceSettings.brand.preview.paragraph', { name: brand.name })],
    details: [
      { label: t('workspaceSettings.brand.preview.dateLabel'), value: t('workspaceSettings.brand.preview.dateValue') },
      { label: t('workspaceSettings.brand.preview.timeLabel'), value: t('workspaceSettings.brand.preview.timeValue') },
    ],
    button: { label: t('workspaceSettings.brand.preview.button'), url: 'https://example.com' },
    footerLines: [t('workspaceSettings.brand.preview.footer')],
  });
}

/** @param {{ colorKey: 'primary' | 'secondary' | 'accent', value: string, onChange: (value: string) => void }} props */
function ColorField({ colorKey, value, onChange }) {
  const [draft, setDraft] = useState(value);
  const normalized = normalizeBrandColor(draft);
  // cor clara no botão ou na faixa: avisa, porque o sistema troca o texto para escuro e o resultado muda
  const isLowContrast = normalized !== null && colorKey === 'primary' && contrastRatio(normalized, '#ffffff') < 3;
  return html`<div class="brand-color-field">
    <label class="brand-color-swatch" style=${`--swatch:${normalized ?? value}`}>
      <input type="color" value=${normalized ?? value} aria-label=${t(`workspaceSettings.brand.colors.${colorKey}`)}
        onInput=${(event) => {
          const next = /** @type {HTMLInputElement} */ (event.currentTarget).value;
          setDraft(next);
          onChange(next);
        }} />
    </label>
    <div class="brand-color-text">
      <span class="form-field-label">${t(`workspaceSettings.brand.colors.${colorKey}`)}</span>
      <span class="brand-color-hint">${t(`workspaceSettings.brand.colors.${colorKey}Hint`)}</span>
      <input class="text-input brand-color-hex" name=${`brand-${colorKey}`} value=${draft} maxLength=${7} spellcheck=${false}
        data-error=${normalized ? undefined : 'true'}
        onInput=${(event) => {
          const next = /** @type {HTMLInputElement} */ (event.currentTarget).value;
          setDraft(next);
          const color = normalizeBrandColor(next);
          if (color) onChange(color);
        }} />
      ${!normalized ? html`<span class="brand-color-error" role="alert">${t('workspaceSettings.brand.colors.invalid')}</span>` : null}
      ${isLowContrast ? html`<span class="brand-color-hint">${t('workspaceSettings.brand.colors.lightPrimary')}</span>` : null}
    </div>
  </div>`;
}

export function BrandIdentitySection() {
  const tenant = /** @type {any} */ (sessionState.value?.tenant);
  const savedColors = readBrandColors(tenant?.brandColors ?? null);
  const [colors, setColors] = useState(savedColors);
  const [isSaving, setIsSaving] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [formKey, setFormKey] = useState(0);
  const fileInputRef = useRef(/** @type {HTMLInputElement | null} */ (null));
  const isDirty = BRAND_COLOR_KEYS.some((key) => colors[key] !== savedColors[key]) || !tenant?.brandColors;

  const previewLogoUrl = usePreviewImageUrl(tenant?.brandLogoUrl);
  const previewIconUrl = usePreviewImageUrl(tenant?.logoUrl);
  const previewHtml = useMemo(() => brandPreviewHtml({
    name: tenant?.displayName ?? '',
    colors,
    logoUrl: previewLogoUrl,
    iconUrl: previewIconUrl,
  }), [colors, previewLogoUrl, previewIconUrl, tenant?.displayName]);

  const saveColors = async () => {
    setIsSaving(true);
    try {
      replaceCurrentTenantLocally(await updateWorkspace({ brandColors: colors }));
      showSuccessSnackbar(t('workspaceSettings.brand.colorsSaved'));
    } catch (error) {
      showErrorSnackbar(error);
    } finally {
      setIsSaving(false);
    }
  };

  const restoreDefaults = () => {
    setColors({ ...DEFAULT_BRAND_COLORS });
    setFormKey((key) => key + 1);
  };

  /** @param {File} file */
  const uploadBrandLogo = async (file) => {
    if (!BRAND_LOGO_TYPES.includes(file.type)) return showErrorSnackbar(new Error(t('workspaceSettings.brand.logo.invalidType')));
    if (file.size > MAXIMUM_BRAND_LOGO_BYTES) return showErrorSnackbar(new Error(t('workspaceSettings.brand.logo.tooLarge')));
    setIsUploading(true);
    try {
      const uploaded = await uploadImage(file);
      replaceCurrentTenantLocally(await updateWorkspace({ brandLogoFileUrl: uploaded.url }));
      showSuccessSnackbar(t('workspaceSettings.brand.logo.saved'));
    } catch (error) {
      showErrorSnackbar(error);
    } finally {
      setIsUploading(false);
    }
    return undefined;
  };

  const removeBrandLogo = async () => {
    try {
      replaceCurrentTenantLocally(await updateWorkspace({ brandLogoFileUrl: null }));
    } catch (error) {
      showErrorSnackbar(error);
    }
  };

  return html`<${SettingsSection} title=${t('workspaceSettings.brand.title')} description=${t('workspaceSettings.brand.description')}>
    <div class="brand-identity">
      <div class="brand-identity-form">
        <div class="brand-logo-field">
          <span class="form-field-label">${t('workspaceSettings.brand.logo.label')}</span>
          <span class="brand-color-hint">${t('workspaceSettings.brand.logo.hint')}</span>
          <div class="brand-logo-box">
            ${tenant?.brandLogoUrl
              ? html`<img src=${tenant.brandLogoUrl} alt=${t('workspaceSettings.brand.logo.label')} />`
              : html`<span class="brand-logo-empty">${t('workspaceSettings.brand.logo.empty')}</span>`}
          </div>
          <div class="brand-logo-actions">
            <input ref=${fileInputRef} type="file" accept=${BRAND_LOGO_TYPES.join(',')} hidden
              onChange=${(event) => {
                const input = /** @type {HTMLInputElement} */ (event.currentTarget);
                const file = input.files?.[0];
                input.value = '';
                if (file) uploadBrandLogo(file);
              }} />
            <${Button} variant="secondary" size="small" icon="IconUpload" disabled=${isUploading}
              title=${tenant?.brandLogoUrl ? t('workspaceSettings.brand.logo.replace') : t('workspaceSettings.brand.logo.upload')}
              onClick=${() => fileInputRef.current?.click()} />
            ${tenant?.brandLogoUrl ? html`<${Button} variant="tertiary" size="small" title=${t('workspaceSettings.brand.logo.remove')} onClick=${removeBrandLogo} />` : null}
          </div>
        </div>
        <div class="brand-color-list" key=${formKey}>
          ${BRAND_COLOR_KEYS.map((key) => html`<${ColorField} key=${key} colorKey=${key} value=${colors[key]}
            onChange=${(/** @type {string} */ value) => setColors((current) => ({ ...current, [key]: value }))} />`)}
        </div>
        <div class="brand-identity-actions">
          <${Button} variant="primary" accent="blue" size="small" title=${t('workspaceSettings.brand.saveColors')} disabled=${!isDirty || isSaving} onClick=${saveColors} />
          <${Button} variant="tertiary" size="small" title=${t('workspaceSettings.brand.restoreDefaults')} onClick=${restoreDefaults} />
        </div>
        <p class="brand-color-hint">${t('workspaceSettings.brand.usage')}</p>
      </div>
      <figure class="brand-preview">
        <figcaption>${t('workspaceSettings.brand.preview.caption')}</figcaption>
        <iframe sandbox="" title=${t('workspaceSettings.brand.preview.caption')} srcdoc=${previewHtml} loading="lazy"></iframe>
      </figure>
    </div>
  <//>`;
}
